import {
  IDM_PASSWORD_WHITELIST,
  applicationMessages,
  applicationModelNames,
  authenticationMethods,
  scimConnectionTypes,
} from 'client/util/Constants/ApplicationConstants';
import { Types } from '@jumpcloud/ui-components';
import { get, memoize } from 'lodash';

import ApplicationApi from 'client/api/v2/ApplicationApi';
import ApplicationTemplateApi from 'client/api/v2/ApplicationTemplateApiV2';

const { OIDC } = applicationModelNames;

export default {
  getFeaturedApps() {
    return [
      'personio',
      'aws-sso',
      'slack',
      'google',
      'office365',
      'atlassiancloud',
      'salesforce',
      'zoom',
      'gitlab',
      'egnyte',
    ];
  },

  hideManualImportApps() {
    return [
      'PrinterLogic SaaS',
      'Sentry',
      'Figma',
    ];
  },

  appStartIndexOne() {
    return [
      'Thrive Learning',
      'Bitwarden',
    ];
  },
};

export function isOIDC(item) {
  return item.name === OIDC || item.sso?.type === OIDC;
}

/**
 * This uses an array of apps names and it should be synchronized with its BE counterpart
 * @see https://github.com/TheJumpCloud/jumpcloud-scim/blob/308427d65712b42b765d2141f8bdb861fd04de0f/config/password_sync.go#L7
 * @param {String} name Application name in DB
 * @returns Bool
 */
export function hasPasswordSyncSupport(name) {
  return IDM_PASSWORD_WHITELIST.includes(name);
}

export function isUrlTest(url) {
  const urlRegex = /(([\w]+:)?\/\/)?(([\d\w]|%[a-fA-f\d]{2,2})+(:([\d\w]|%[a-fA-f\d]{2,2})+)?@)?([\d\w][-\d\w]{0,253}[\d\w]\.)+[\w]{2,63}(:[\d]+)?(\/([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)*(\?(&?([-+_~.\d\w]|%[a-fA-f\d]{2,2})=?)*)?(#([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)?/;
  return urlRegex.test(url);
}

/**
 * HTTPS Input validation
 * @param {String} url Input value
 */
export function isSecureUrlTest(url) {
  let urlValue;
  try {
    urlValue = new URL(url);
  } catch (_) {
    return false;
  }
  return urlValue.protocol === 'https:';
}

/**
 * DisplayLabel input validation
 * @param {String} value Input value
 */
export function isDisplayLabelValid(value) {
  const regexExp = /^[^<>&/\\'"]*$/;
  return regexExp.test(value);
}

/**
 *
 * @param {string} type Indicates type of connection, example 'oauth'
 */
export function getMissingStepsNoticeText(type = authenticationMethods.BEARER) {
  const { OAUTH2 } = authenticationMethods;

  const textsMap = {
    [OAUTH2]: 'This section will be unlocked after a successful authorization.',
  };

  if (textsMap[type]) {
    return textsMap[type];
  }

  return 'This section will be unlocked once a successful test connection has been established.';
}

/**
 * Vee validator rule to test https strings
 * @param {*} validator Dependency Injection of the Vee validate object
 */
export function isSecureUrl(validator) {
  validator.extend('isSecureUrl', {
    getMessage: applicationMessages.get('notSecureUrl'),
    validate: isSecureUrlTest,
  });
}

/**
 * Vee validator rule to test enpoint paths strings
 * @param {*} validator Dependency Injection of the Vee validate object
 */
export function isEndpoint(validator) {
  validator.extend('isEndpoint', {
    getMessage(field) {
      return `${field} must be valid endpoint path`;
    },
    validate(value) {
      const decoded = decodeURIComponent(value);
      const startsWithSlash = /^[/]/i.test(decoded);
      const isEndpointPath = /^(?!\w+(:\/+|\.))(\/?:?[\w\-_.]+\/?)+(?!\?)$/g.test(decoded);

      return startsWithSlash && isEndpointPath;
    },
  });
}

/**
 * Vee validator rule to test JSON path strings
 * @param {*} validator Dependency Injection of the Vee validate object
 */
export function isJSONPath(validator) {
  validator.extend('isJSONPath', {
    getMessage(field) {
      return `${field} must be a valid JSON path`;
    },
    validate(value) {
      if (value.length === 1) {
        return (value[0] === '.');
      }
      // No spaces should be allowed in JSON path dot notation
      const noSpaces = /^\S*$/.test(value);
      // Dots, underscores, opening and closing square brackets
      // should be the only special characters allowed
      const onlyAllowedCharacters = /^[a-zA-Z0-9._[\]]+$/.test(value);
      // A JSON path should not start or end with a dot, nor have duplicate dots
      const noStartStopOrDupDot = /^(?!\.)(?!.*\.$)(?!.*?\.\.)[a-zA-Z0-9_.[\]]+$/.test(value);

      return noSpaces && onlyAllowedCharacters && noStartStopOrDupDot;
    },
  });
}

/**
 * Vee validator rule to test HTTP Header strings
 * @param {*} validator Dependency Injection of the Vee validate object
 */
export function isHTTPHeader(validator) {
  validator.extend('isHTTPHeader', {
    getMessage(field) {
      return `${field} must be valid HTTP Header`;
    },
    validate(value) {
      // No spaces should be allowed in an HTTP header
      const noSpaces = /^\S*$/.test(value);
      // Dash and underscores should be the only special characters allowed
      const onlyDashAndUnderscore = /^[a-zA-Z0-9-_]+$/.test(value);

      return noSpaces && onlyDashAndUnderscore;
    },
  });
}

/**
 * Vee validator rule to test HTTP URL variables
 * @param {*} validator Dependency Injection of the Vee validate object
 */
export function isValidQuery(validator) {
  validator.extend('isValidQuery', {
    getMessage(field) {
      return `${field} must have valid URL variables`;
    },
    validate(value) {
      if (value[0] === '?') {
        return false;
      }

      try {
        const query = new URLSearchParams(value);
        const values = Array.from(query.values());
        // Check that they actually entered somthing meaningful
        return values.filter(val => Boolean(val)).length > 0;
      } catch {
        return false;
      }
    },
  });
}

export function isOauthScopesString(validator) {
  validator.extend('isOauthScopesString', {
    getMessage() {
      return 'Invalid characters. Special characters are not supported and values must be separated by spaces.';
    },
    validate(value) {
      const cleaned = value.trim();
      const accepted = /^([\x21\x23-\x5B\x5D-\x7E]+( +[\x21\x23-\x5B\x5D-\x7E]+)*)?$/gi;
      return accepted.test(cleaned);
    },
  });
}

/**
 * Utility function to sort items alphabetically by Strings
 * @param {Array<String|Object>} source
 * Array of Strings to be sorted (sortByProperty NOT NEEDED)
 * Array of Objects to be sorted (sortByProperty NEEDED)
 * @param {Array} sortByProperty Array of CSVs as path on the object to the sortByProperty
 * Example: ['item', 'name'] -> Sorts by obj.item.name
 */
export function sortAlphabetically(source = [], sortByProperty = []) {
  try {
    return source.sort((a, b) => {
      const hasSortProperty = sortByProperty.length;
      const aSortValue = hasSortProperty ? get(a, sortByProperty).toLowerCase() : a.toLowerCase();
      const bSortValue = hasSortProperty ? get(b, sortByProperty).toLowerCase() : b.toLowerCase();

      if (aSortValue < bSortValue) {
        return -1;
      } if (aSortValue > bSortValue) {
        return 1;
      }
      return 0;
    });
  } catch (err) {
    // Fail silently
    return source;
  }
}

/**
 * Fetches an application template using its name
 * @param {string} appName target application name
 * @returns Promise
 */
export async function fetchApplicationTemplateByName(appName) {
  const error = Error('No application template found.');
  try {
    const { results } = await ApplicationTemplateApi.fetch(0, 5, 'name', appName);
    const foundTemplate = results.find(appTemplate => appTemplate.name === appName);

    if (foundTemplate) return Promise.resolve(foundTemplate);
    throw error;
  } catch {
    throw error;
  }
}

const memoizedfetchApplicationTemplateByName = memoize(fetchApplicationTemplateByName);

/**
 * Function that takes an array of string app names and an array of key names,
 * fetches their templates and returns an array of objects with specified keys
 * @param {string[]} targetApps Array of application names
 * @param {string[]} keys Array of needed property names
 * @returns [..., { <appname>: <specified app template keys> }]
 */
export async function getApplicationTemplatesByName(targetApps = [], keys = []) {
  const requests = targetApps.map(targetApp => memoizedfetchApplicationTemplateByName(targetApp));
  const resolvedPromises = await Promise.allSettled(requests);

  const apps = resolvedPromises.reduce((acc, promise) => {
    if (!promise.value) return acc;

    const temp = {};
    const appTemplate = promise.value;

    keys.forEach(key => {
      if (appTemplate[key]) temp[key] = appTemplate[key];
    });

    const matchingAppName = targetApps.find(appName => appName === appTemplate.name);
    acc.push({ [matchingAppName]: temp });

    return acc;
  }, []);

  return apps;
}

export async function getAppTemplateRoute(appName) {
  try {
    const template = await fetchApplicationTemplateByName(appName);
    return `#/sso/configure/${template._id}`;
  } catch {
    return '';
  }
}

// new scim import modal functions

const scimUsersImportProps = {
  importStatus: 'importStatus',
  lastname: 'lastname',
  firstname: 'firstname',
  email: 'email',
  creationSource: 'creationSource',
  userStatusInSourceProvider: 'userStatusInSourceProvider',
};

const { Column } = Types;

/**
 * Takes in an ApplicationModel -> item and evaluates what property to use
 * for the display name (the name that shows up in the Import Users modal flow).
 * @param {*} item SSO/SCIM Item from ApplicationModel
 * @returns {string} Application Display name
 */
export function getApplicationDisplayName({ displayLabel, displayName, name }) {
  return (name === 'bookmark' || name === 'saml2') ? displayLabel : displayName;
}

/**
 * Gets the provider source name from provider data in ApplicationModel -> item.
 * @param {object} item ApplicationModel item
 * @param {string} scimConnectionType ApplicationModel scimConnectionType
 * @returns {string} appSourceName
 */
export function getApplicationImportSourceName(item, scimConnectionType) {
  const isCustomRestApp = scimConnectionType === scimConnectionTypes.CUSTOM;
  const isCustomSamlApp = item.name === 'saml2';
  const isPrebuiltIntegration = !!item.provision;
  let appSourceName = '';

  if (item.isBookmark) appSourceName = `bookmark:${item.displayLabel}`;
  if (isCustomRestApp) appSourceName = `${item.displayLabel}`;
  if (isCustomSamlApp) appSourceName = `saml:${item.displayLabel}`;
  if (isPrebuiltIntegration || appSourceName === '') appSourceName = item.displayName;

  return appSourceName;
}

export async function listApplicationsByName(appName) {
  const response = await ApplicationApi.fetchApplicationsByName(appName);

  return response;
}

/**
 * Generates column data for ItemListPagination component used in MigratedScimUserImport.vue
 * @returns {{dataFieldName: string, label: string, width: string}[]} Column data array
 */
export function getItemListColumns() {
  return [
    Column({
      dataFieldName: scimUsersImportProps.importStatus,
      label: 'Import Status',
      width: '.5fr',
    }),
    Column({
      dataFieldName: scimUsersImportProps.lastname,
      label: 'Last Name',
      width: '.6fr',
    }),
    Column({
      dataFieldName: scimUsersImportProps.firstname,
      label: 'First Name',
      width: '.6fr',
    }),
    Column({
      dataFieldName: scimUsersImportProps.email,
      label: 'Email',
      width: '1fr',
    }),
    Column({
      dataFieldName: scimUsersImportProps.creationSource,
      label: 'Creation Source',
      width: '.6fr',
    }),
  ];
}

/**
 * Returns the SSO URL for the current environment
 * @returns {string} SSO URL
 */
export function getSsoUrl() {
  const currentUrl = window.location.origin;
  return currentUrl.replace('console', 'sso');
}

/**
 * Vee-validate 3 apps rules
 */
export const validationRules = {
  isSecureUrl: {
    name: 'isSecureUrl',
    config: {
      validate: isSecureUrlTest,
      message: applicationMessages.get('notSecureUrl'),
    },
  },

  isUrl: {
    name: 'isUrl',
    config: {
      validate: isUrlTest,
      message: applicationMessages.get('notUrl'),
    },
  },

  isDisplayLabelValid: {
    name: 'isDisplayLabelValid',
    config: {
      validate: isDisplayLabelValid,
      message: applicationMessages.get('displayLabelInvalid'),
    },
  },
};
